import type { InitOptions } from "i18next";
import type { HttpBackendOptions } from "i18next-http-backend";
import i18n, { createInstance } from "i18next";
import { initReactI18next } from "react-i18next";

import type { BlogLocale } from "@/features/blog/locales";
import landingEn from "../assets/locales/en/landing.json";
import translationEn from "../assets/locales/en/translations.json";
import landingEs from "../assets/locales/es/landing.json";
import landingFr from "../assets/locales/fr/landing.json";
import { isProduction } from "./utils";

export const defaultNS = "translations";
export const resources = {
  en: { translations: translationEn },
} as const;
export const languages: Record<keyof typeof resources, string> = {
  en: "English",
};

/** Landing namespace resources — bundled for SSR (no async load). */
const LANDING_RESOURCES: Record<BlogLocale, { landing: object }> = {
  en: { landing: landingEn },
  fr: { landing: landingFr },
  es: { landing: landingEs },
  ro: { landing: landingEn },
  de: { landing: landingEn },
};

/**
 * Creates a request-scoped i18next instance for a landing-page locale.
 * Use inside the landing layout so concurrent SSR requests don't clobber
 * each other's language. Resources are bundled, so no async loading.
 *
 * Note: we intentionally do NOT `.use(initReactI18next)` here — that
 * plugin sets a module-level global in react-i18next, which would
 * overwrite the authed-app singleton registered in this file and make
 * its `useTranslation()` calls resolve against the landing namespace.
 * The I18nextProvider in `_landing.tsx` supplies this instance via
 * React context, which takes precedence over the global anyway.
 */
export function createLandingI18n(locale: BlogLocale) {
  const instance = createInstance();
  void instance.init({
    lng: locale,
    fallbackLng: "en",
    ns: ["landing"],
    defaultNS: "landing",
    supportedLngs: Object.keys(LANDING_RESOURCES),
    resources: LANDING_RESOURCES,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });
  return instance;
}

const sharedOptions: InitOptions = {
  defaultNS,
  lng: "en",
  load: "languageOnly",
  supportedLngs: Object.keys(resources),
  ns: [defaultNS],
  debug: !isProduction,
  fallbackLng: "en",
  interpolation: {
    escapeValue: false, // not needed for react as it escapes by default
  },
  resources,
};

if (typeof window === "undefined") {
  // SSR: init with bundled resources only (no browser plugins)
  void i18n.use(initReactI18next).init(sharedOptions);
} else {
  // Client: add language detector and HTTP backend for dynamic loading
  const [{ default: LanguageDetector }, { default: HttpBackend }] =
    await Promise.all([
      import("i18next-browser-languagedetector"),
      import("i18next-http-backend"),
    ]);

  void i18n
    .use(initReactI18next)
    .use(LanguageDetector)
    .use(HttpBackend)
    .init<HttpBackendOptions>({
      ...sharedOptions,
      saveMissing: !isProduction,
      backend: {
        loadPath: isProduction
          ? "/locales/{{lng}}/{{ns}}.json"
          : "/src/assets/locales/{{lng}}/{{ns}}.json",
      },
    });
}
